import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { LiveChatMessage, LiveChatService, LiveChatSession, LiveChatTransferUser } from '../../../core/services/live-chat.service';
import { restoreCsrTheme } from '../../csr/csr-theme';

@Component({
  selector: 'app-supervisor-live-chats',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './supervisor-live-chats.component.html',
  styleUrls: ['./supervisor-live-chats.component.scss'],
})
export class SupervisorLiveChatsComponent implements OnInit, OnDestroy {
  sessions: LiveChatSession[] = [];
  messages: LiveChatMessage[] = [];
  selected: LiveChatSession | null = null;
  viewMode: 'active' | 'history' = 'active';
  query = '';
  loading = false;
  toast = '';
  transferOpen = false;
  transferTargetUserId: number | null = null;
  transferUsers: LiveChatTransferUser[] = [];
  transferLoading = false;
  transferring = false;
  private sub?: Subscription;
  private pollTimer: any = null;
  private toastTimer: any = null;

  get filteredSessions() {
    const q = this.query.trim().toLowerCase();
    if (!q) return this.sessions;
    return this.sessions.filter(s =>
      [s.citizenName, s.citizenMobile, s.assignedAgentName, s.assignedAgentExtension, s.sourceQuestion, s.status]
        .some(v => String(v ?? '').toLowerCase().includes(q))
    );
  }

  get activeCount() { return this.sessions.filter(s => s.status !== 'CLOSED').length; }
  get availableTransferUsers() {
    const assignedUserId = Number(this.selected?.assignedAgentUserId);
    return this.transferUsers.filter(u => Number(u.userId) !== assignedUserId);
  }

  constructor(private liveChat: LiveChatService) {}

  ngOnInit() {
    restoreCsrTheme();
    this.loadSessions();
    this.liveChat.connect();
    this.sub = this.liveChat.events$.subscribe(event => {
      if (event.session) this.upsertSession(event.session);
      if (event.type === 'SESSION_TRANSFERRED' && event.session && event.session.id === this.selected?.id) {
        this.selected = event.session;
        this.loadMessages(event.session.id);
      }
      if (event.message && this.selected?.id === event.message.sessionId) this.loadMessages(this.selected.id);
      if (event.type === 'SESSION_STARTED') this.showToast('New live chat started');
      if (event.type === 'SESSION_TRANSFERRED') this.showToast('Live chat transferred');
    });
    this.pollTimer = setInterval(() => this.loadSessions(false), 15000);
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }

  setViewMode(mode: 'active' | 'history') {
    if (this.viewMode === mode) return;
    this.viewMode = mode;
    this.selected = null;
    this.messages = [];
    this.loadSessions();
  }

  loadSessions(showLoading = true) {
    if (showLoading) this.loading = true;
    const request = this.viewMode === 'history'
      ? this.liveChat.supervisorHistory()
      : this.liveChat.supervisorActiveSessions();
    request.subscribe({
      next: res => {
        this.sessions = this.liveChat.unwrapList<LiveChatSession>(res);
        if (!this.selected && this.sessions.length) this.selectSession(this.sessions[0]);
        if (this.selected && !this.sessions.some(s => s.id === this.selected?.id)) {
          this.selected = null;
          this.messages = [];
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.showToast('Unable to load live chats');
      }
    });
  }

  selectSession(session: LiveChatSession) {
    this.selected = session;
    this.loadMessages(session.id);
  }

  loadMessages(sessionId: number) {
    this.liveChat.messages(sessionId).subscribe({
      next: res => this.messages = this.liveChat.unwrapList<LiveChatMessage>(res),
      error: () => this.showToast('Unable to load conversation')
    });
  }

  openTransfer() {
    if (!this.selected || this.selected.status === 'CLOSED') return;
    this.transferOpen = !this.transferOpen;
    if (this.transferOpen && this.transferUsers.length === 0) this.loadTransferUsers();
  }

  loadTransferUsers() {
    this.transferLoading = true;
    this.liveChat.internalUsers().subscribe({
      next: res => {
        this.transferUsers = this.liveChat.unwrapList<LiveChatTransferUser>(res);
        this.transferLoading = false;
      },
      error: () => {
        this.transferLoading = false;
        this.showToast('Unable to load agents');
      }
    });
  }

  transferChat() {
    if (!this.selected || !this.transferTargetUserId || this.transferring) return;
    this.transferring = true;
    this.liveChat.transfer(this.selected.id, Number(this.transferTargetUserId)).subscribe({
      next: res => {
        const updated = this.liveChat.unwrap<LiveChatSession>(res);
        this.upsertSession(updated);
        this.selected = updated;
        this.transferOpen = false;
        this.transferTargetUserId = null;
        this.transferring = false;
        this.loadMessages(updated.id);
        this.showToast('Chat transferred');
      },
      error: () => {
        this.transferring = false;
        this.showToast('Unable to transfer chat');
      }
    });
  }

  formatTime(value?: string) {
    if (!value) return '-';
    return new Date(value).toLocaleString();
  }

  private upsertSession(session: LiveChatSession) {
    const belongs = this.viewMode === 'history' ? session.status === 'CLOSED' : session.status !== 'CLOSED';
    const idx = this.sessions.findIndex(s => s.id === session.id);
    if (!belongs) {
      if (idx >= 0) this.sessions.splice(idx, 1);
    } else if (idx >= 0) {
      this.sessions[idx] = session;
    } else {
      this.sessions.unshift(session);
    }
    this.sessions = [...this.sessions].sort((a, b) =>
      new Date(b.lastMessageAt || b.createdAt || 0).getTime() - new Date(a.lastMessageAt || a.createdAt || 0).getTime()
    );
  }

  private showToast(message: string) {
    this.toast = message;
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => this.toast = '', 2500);
  }
}
